/**
 * Google Places API (New) Integration Services
 * Reference: https://developers.google.com/maps/documentation/places/web-service/place-autocomplete
 *
 * Note: Uses axios directly instead of project's request utility to avoid
 * internal headers (like 'headerVariant') being sent to Google's API
 */

import axios from 'axios';
import {
  AutocompletePrediction,
  PlaceResult,
  AutocompleteRequestBody,
  PlaceDetailsRequest,
  AutocompleteOptions,
} from './types';
import { mapFieldsToFieldMask, transformAutocompleteSuggestions, transformPlaceDetails, isValidInput } from './utils';

// ==================== Constants ====================

const PLACES_API_BASE_URL = 'https://places.googleapis.com/v1';

// ==================== API Functions ====================

/**
 * Fetches autocomplete predictions from Google Places API (New)
 *
 * @param input - Search query string
 * @param options - Configuration options (API key, country, types) - all required
 * @returns Promise with array of autocomplete predictions
 * @throws Axios error if request fails - should be handled by mutation
 *
 * @example
 * ```typescript
 * const predictions = await fetchAutocompletePredictions('New York', {
 *   apiKey: 'YOUR_API_KEY',
 *   countryRestriction: 'US',
 *   types: ['locality']
 * });
 * ```
 */
export async function fetchAutocompletePredictions(
  input: string,
  options: AutocompleteOptions,
): Promise<AutocompletePrediction[]> {
  // Early return for empty input
  if (!isValidInput(input)) {
    return [];
  }

  // Build request body
  const requestBody: AutocompleteRequestBody = {
    input: input.trim(),
  };

  // Add country restriction if specified
  if (options.countryRestriction) {
    requestBody.includedRegionCodes = [options.countryRestriction];
  }

  // Add type filter if specified
  if (options.types && options.types.length > 0) {
    requestBody.includedPrimaryTypes = options.types;
  }

  // Make API request using axios directly (avoids internal headers from request wrapper)

  const response = await axios({
    url: `${PLACES_API_BASE_URL}/places:autocomplete`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': options.apiKey,
    },
    data: requestBody,
  });

  // Transform and return response
  return transformAutocompleteSuggestions(response.data.suggestions || []);
}

/**
 * Fetches place details from Google Places API (New)
 *
 * @param placeRequest - Place details request with place ID and optional fields - all required
 * @param apiKey - Google Maps API key - required
 * @returns Promise with place details
 * @throws Axios error if request fails - should be handled by mutation
 *
 * @example
 * ```typescript
 * const placeDetails = await fetchPlaceDetails(
 *   { placeId: 'ChIJOwg_06VPwokRYv534QaPC8g', fields: ['name', 'formatted_address'] },
 *   'YOUR_API_KEY'
 * );
 * ```
 */
export async function fetchPlaceDetails(placeRequest: PlaceDetailsRequest, apiKey: string): Promise<PlaceResult> {
  // Map fields to REST API field mask
  const fieldMask = mapFieldsToFieldMask(placeRequest.fields);

  const response = await axios({
    url: `${PLACES_API_BASE_URL}/places/${placeRequest.placeId}`,
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': apiKey,
      'X-Goog-FieldMask': fieldMask,
    },
  });

  // Transform and return response
  return transformPlaceDetails(response.data, placeRequest.placeId);
}
